import { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime } from "@/lib/datetime";
import { toast } from "sonner";
import {
    Phone, PhoneIncoming, PhoneOutgoing, CheckCircle, XCircle,
    Clock, Timer, ChartBar, Trophy, TrendUp, TrendDown, Target, DownloadSimple, GridFour,
    Robot, ArrowBendUpRight, CalendarCheck, PhoneCall, Note,
    WhatsappLogo, ChatCircleText, UserPlus, Broadcast,
} from "@phosphor-icons/react";
import {
    ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
    PieChart, Pie, Cell, Legend,
} from "recharts";

const RANGES = [
    { id: "today", label: "Today" },
    { id: "7d", label: "7 days" },
    { id: "30d", label: "30 days" },
    { id: "all", label: "All time" },
];

const BRAND = "#FF5A36";
const INK = "#0A0A0A";
const EMERALD = "#10b981";
const SKY = "#00A1E0";
const STONE = "#a8a29e";
const DISP_COLORS = ["#FF5A36", "#10b981", "#00A1E0", "#8b5cf6", "#f59e0b", "#ef4444", "#0A0A0A", "#64748b"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const PROVIDER_META = {
    twilio: { label: "Twilio", color: "#F22F46" },
    plivo: { label: "Plivo", color: "#12B76A" },
    exotel: { label: "Exotel", color: "#6C5CE7" },
    mock: { label: "Sandbox", color: "#a8a29e" },
    unknown: { label: "Other", color: "#a8a29e" },
};

function fmtDur(s) {
    if (!s) return "0:00";
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = String(s % 60).padStart(2, "0");
    if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
    return `${m}:${sec}`;
}
function fmtDateShort(iso) {
    try {
        return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch { return iso; }
}
function pct(part, whole) {
    if (!whole) return 0;
    return Math.round((part / whole) * 100);
}
function deltaPct(cur, prev) {
    if (prev == null) return null;
    if (prev === 0) return cur > 0 ? 100 : 0;
    return Math.round(((cur - prev) / prev) * 100);
}

const DeltaChip = ({ delta, positiveIsGood = true }) => {
    if (delta == null) return null;
    const up = delta >= 0;
    const good = positiveIsGood ? up : !up;
    const Icon = up ? TrendUp : TrendDown;
    return (
        <span
            className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular ${
                good ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"
            }`}
        >
            <Icon size={10} weight="bold" /> {Math.abs(delta)}%
        </span>
    );
};

const KPICard = ({ label, value, sub, icon: Icon, accent, delta, positiveIsGood, testid }) => (
    <div className="rounded-2xl border border-stone-200 bg-white p-5 transition-shadow hover:shadow-sm" data-testid={testid}>
        <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone2">{label}</span>
            <Icon size={16} weight="fill" style={{ color: accent || STONE }} />
        </div>
        <div className="mt-3 flex items-end gap-2">
            <div className="font-heading text-3xl font-bold leading-none tabular">{value}</div>
            <div className="mb-0.5"><DeltaChip delta={delta} positiveIsGood={positiveIsGood} /></div>
        </div>
        {sub && <div className="mt-1.5 text-[11px] text-stone2">{sub}</div>}
    </div>
);

function ProviderBreakdown({ providers }) {
    const list = (providers || []).filter((p) => p.total > 0);
    if (list.length === 0) {
        return <div className="grid h-40 place-items-center text-center text-sm text-stone2">No calls in this period.</div>;
    }
    const max = Math.max(...list.map((p) => p.total));
    return (
        <div className="space-y-4" data-testid="provider-breakdown-list">
            {list.map((p) => {
                const meta = PROVIDER_META[p.provider] || PROVIDER_META.unknown;
                const rate = pct(p.completed, p.total);
                return (
                    <div key={p.provider} data-testid={`provider-row-${p.provider}`}>
                        <div className="mb-1.5 flex items-center justify-between">
                            <span className="flex items-center gap-2 text-sm font-semibold">
                                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: meta.color }} />
                                {meta.label}
                            </span>
                            <span className="tabular text-sm font-bold">{p.total}</span>
                        </div>
                        <div className="h-2 overflow-hidden rounded-full bg-stone-100">
                            <div className="h-full rounded-full transition-all" style={{ width: `${(p.total / max) * 100}%`, backgroundColor: meta.color }} />
                        </div>
                        <div className="mt-1 flex items-center justify-between text-[11px] text-stone2">
                            <span>{rate}% answered</span>
                            <span className="tabular">{fmtDur(p.total_duration_sec)} talk</span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function Heatmap({ data }) {
    const { grid, max } = useMemo(() => {
        const g = Array.from({ length: 7 }, () => Array(24).fill(0));
        let m = 0;
        (data || []).forEach((h) => {
            const r = (h.dow || 1) - 1, c = h.hour || 0;
            if (r >= 0 && r < 7 && c >= 0 && c < 24) { g[r][c] += h.count; m = Math.max(m, g[r][c]); }
        });
        return { grid: g, max: m };
    }, [data]);

    if (!max) return <div className="grid h-40 place-items-center text-sm text-stone2">No activity data yet.</div>;

    return (
        <div className="overflow-x-auto" data-testid="hours-heatmap">
            <div className="min-w-[520px]">
                <div className="flex gap-[3px] pl-9">
                    {Array.from({ length: 24 }).map((_, h) => (
                        <div key={h} className="w-[15px] text-center text-[8px] text-stone-400">{h % 6 === 0 ? h : ""}</div>
                    ))}
                </div>
                {grid.map((row, r) => (
                    <div key={r} className="mt-[3px] flex items-center gap-[3px]">
                        <div className="w-9 text-[10px] font-medium text-stone2">{DOW[r]}</div>
                        {row.map((v, c) => (
                            <div
                                key={c}
                                title={`${DOW[r]} ${c}:00 — ${v} call${v === 1 ? "" : "s"}`}
                                className="h-[15px] w-[15px] rounded-[3px]"
                                style={{ backgroundColor: v ? BRAND : "#f5f5f4", opacity: v ? 0.25 + 0.75 * (v / max) : 1 }}
                            />
                        ))}
                    </div>
                ))}
                <div className="mt-3 flex items-center gap-1.5 pl-9 text-[10px] text-stone2">
                    Less
                    {[0.15, 0.4, 0.65, 0.9].map((o) => (
                        <span key={o} className="h-[10px] w-[10px] rounded-[2px]" style={{ backgroundColor: BRAND, opacity: o }} />
                    ))}
                    More
                </div>
            </div>
        </div>
    );
}

function AiStatsSection({ range }) {
    const { orgTimezone } = useAuth();
    const [d, setD] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let m = true;
        setLoading(true);
        api.get("/ai/stats", { params: { range } })
            .then(({ data }) => { if (m) setD(data); })
            .catch(() => { if (m) setD(null); })
            .finally(() => { if (m) setLoading(false); });
        return () => { m = false; };
    }, [range]);

    const k = d?.kpis || {};
    const sent = d?.sentiment || { positive: 0, neutral: 0, negative: 0 };
    const acts = d?.actions || { case: 0, appointment: 0, callback: 0 };
    const sentTotal = sent.positive + sent.neutral + sent.negative;
    const cards = [
        { label: "AI-handled calls", value: k.total_ai_calls || 0, icon: Robot, color: "#8b5cf6" },
        { label: "Deflection rate", value: `${k.deflection_rate || 0}%`, icon: Target, color: EMERALD, hint: "Resolved without an agent" },
        { label: "Escalated to agent", value: k.escalated || 0, icon: ArrowBendUpRight, color: BRAND },
        { label: "Actions taken", value: k.actions_total || 0, icon: CheckCircle, color: SKY },
    ];

    return (
        <div className="mt-10" data-testid="ai-stats-section">
            <div className="mb-4 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">
                <Robot size={14} weight="fill" /> AI Assistant
            </div>
            {loading ? (
                <div className="rounded-2xl border border-stone-200 bg-white p-8 text-sm text-stone2" data-testid="ai-stats-loading">Loading AI stats…</div>
            ) : !d || (k.total_ai_calls || 0) === 0 ? (
                <div className="rounded-2xl border border-dashed border-stone-300 p-8 text-center text-sm text-stone2" data-testid="ai-stats-empty">
                    No AI-assistant calls in this period yet. Once callers use an AI-routed menu option, their stats and summaries appear here.
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                        {cards.map((c) => {
                            const Icon = c.icon;
                            return (
                                <div key={c.label} data-testid={`ai-kpi-${c.label}`} className="rounded-2xl border border-stone-200 bg-white p-4">
                                    <div className="flex items-center gap-2">
                                        <span className="grid h-8 w-8 place-items-center rounded-lg" style={{ backgroundColor: `${c.color}1a`, color: c.color }}><Icon size={16} weight="fill" /></span>
                                        <span className="text-[11px] font-semibold uppercase tracking-wider text-stone2">{c.label}</span>
                                    </div>
                                    <div className="mt-2 font-heading text-3xl font-bold tracking-tight">{c.value}</div>
                                    {c.hint && <div className="mt-0.5 text-[11px] text-stone2">{c.hint}</div>}
                                </div>
                            );
                        })}
                    </div>

                    <div className="mt-4 grid gap-4 lg:grid-cols-2">
                        {/* Actions breakdown */}
                        <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="ai-actions-breakdown">
                            <div className="text-sm font-semibold text-ink">Actions taken in Salesforce</div>
                            <div className="mt-4 space-y-3">
                                {[{ label: "Cases created", v: acts.case, icon: Note }, { label: "Appointments booked", v: acts.appointment, icon: CalendarCheck }, { label: "Callbacks requested", v: acts.callback, icon: PhoneCall }].map((a) => {
                                    const Icon = a.icon;
                                    return (
                                        <div key={a.label} className="flex items-center justify-between rounded-lg bg-stone-50 px-3 py-2">
                                            <span className="flex items-center gap-2 text-sm text-ink"><Icon size={16} className="text-stone2" /> {a.label}</span>
                                            <span className="tabular font-heading text-lg font-bold">{a.v}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        {/* Sentiment split */}
                        <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="ai-sentiment">
                            <div className="text-sm font-semibold text-ink">Caller sentiment</div>
                            <div className="mt-4 space-y-3">
                                {[{ label: "Positive", v: sent.positive, color: EMERALD }, { label: "Neutral", v: sent.neutral, color: STONE }, { label: "Negative", v: sent.negative, color: "#ef4444" }].map((s) => (
                                    <div key={s.label}>
                                        <div className="mb-1 flex items-center justify-between text-xs">
                                            <span className="font-medium text-ink">{s.label}</span>
                                            <span className="tabular text-stone2">{s.v}</span>
                                        </div>
                                        <div className="h-2 overflow-hidden rounded-full bg-stone-100">
                                            <div className="h-full rounded-full" style={{ width: `${sentTotal ? (s.v / sentTotal) * 100 : 0}%`, backgroundColor: s.color }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Recent AI calls with summaries */}
                    <div className="mt-4 rounded-2xl border border-stone-200 bg-white p-5" data-testid="ai-recent">
                        <div className="text-sm font-semibold text-ink">Recent AI conversations</div>
                        <div className="mt-3 divide-y divide-stone-100">
                            {(d.recent || []).map((r, i) => (
                                <div key={i} className="py-3" data-testid={`ai-recent-${i}`}>
                                    <div className="flex flex-wrap items-center gap-2 text-xs">
                                        <span className="font-semibold text-ink">{r.from_number || "Unknown"}</span>
                                        {r.created_at && <span className="text-stone-400" data-testid={`ai-recent-time-${i}`}>{formatDateTime(r.created_at, orgTimezone)}</span>}
                                        {r.queue_name && <span className="rounded-full bg-stone-50 px-2 py-0.5 text-stone-500">{r.queue_name}</span>}
                                        {r.disposition && <span className="rounded-full bg-stone-100 px-2 py-0.5 text-stone2">{r.disposition}</span>}
                                        <span className={`rounded-full px-2 py-0.5 font-medium ${r.resolution === "resolved" ? "bg-emerald-50 text-emerald-600" : r.resolution === "escalated" || r.resolution === "transferred" ? "bg-orange-50 text-orange-600" : "bg-stone-100 text-stone2"}`}>{r.resolution}</span>
                                        <span className={`rounded-full px-2 py-0.5 font-medium ${r.sentiment === "positive" ? "bg-emerald-50 text-emerald-600" : r.sentiment === "negative" ? "bg-red-50 text-red-600" : "bg-stone-100 text-stone2"}`}>{r.sentiment}</span>
                                        {(r.actions || []).map((a, j) => <span key={j} className="rounded-full bg-sky-50 px-2 py-0.5 font-medium text-sky-600">{a.kind}{a.ref ? ` ${a.ref}` : ""}</span>)}
                                    </div>
                                    {r.summary && <p className="mt-1.5 text-sm text-stone2">{r.summary}</p>}
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}


function WhatsAppReportSection({ range }) {
    const { user } = useAuth();
    const [wa, setWa] = useState(null);
    useEffect(() => {
        let m = true;
        api.get("/reports/whatsapp", { params: { range } })
            .then(({ data }) => { if (m) setWa(data); })
            .catch(() => { if (m) setWa(null); });
        return () => { m = false; };
    }, [range]);
    if (!user?.features?.whatsapp) return null;
    const m = wa?.messages || {}; const b = wa?.broadcasts || {}; const j = wa?.journeys || {};
    return (
        <div className="mt-10" data-testid="wa-stats-section">
            <div className="mb-4 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-emerald-600">
                <WhatsappLogo size={14} weight="fill" /> WhatsApp
            </div>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <KPICard label="Messages" value={m.total ?? 0} sub={`${m.inbound ?? 0} in · ${m.outbound ?? 0} out`} icon={ChatCircleText} accent={EMERALD} testid="wa-kpi-messages" />
                <KPICard label="Contacts reached" value={m.contacts ?? 0} icon={UserPlus} accent={SKY} testid="wa-kpi-contacts" />
                <KPICard label="Broadcasts" value={b.count ?? 0} sub={`${b.recipients ?? 0} recipients`} icon={Broadcast} accent={BRAND} testid="wa-kpi-broadcasts" />
                <KPICard label="Captured leads" value={wa?.captured_leads ?? 0} icon={UserPlus} accent={EMERALD} testid="wa-kpi-leads" />
            </div>
            <div className="mt-4 grid gap-4 lg:grid-cols-3">
                <div className="rounded-2xl border border-stone-200 bg-white p-5 lg:col-span-2" data-testid="wa-broadcast-perf">
                    <div className="mb-4 text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Broadcast performance</div>
                    <div className="grid grid-cols-3 gap-4 text-center sm:grid-cols-6">
                        {[["Recipients", b.recipients], ["Sent", b.sent], ["Delivered", b.delivered], ["Read", b.read], ["Replied", b.replied], ["Failed", b.failed]].map(([lbl, v]) => (
                            <div key={lbl}><div className="font-heading text-2xl font-bold tabular">{v ?? 0}</div><div className="text-[10px] uppercase tracking-wider text-stone2">{lbl}</div></div>
                        ))}
                    </div>
                    <div className="mt-4 flex gap-6 border-t border-stone-100 pt-4 text-sm">
                        <div><span className="font-heading text-lg font-bold text-emerald-600">{b.read_rate ?? 0}%</span> <span className="text-stone2">read rate</span></div>
                        <div><span className="font-heading text-lg font-bold text-emerald-600">{b.reply_rate ?? 0}%</span> <span className="text-stone2">reply rate</span></div>
                    </div>
                </div>
                <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="wa-journeys-card">
                    <div className="mb-4 text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Journeys</div>
                    <div className="flex gap-8">
                        <div><div className="font-heading text-3xl font-bold tabular text-emerald-600">{j.active ?? 0}</div><div className="text-xs text-stone2">active</div></div>
                        <div><div className="font-heading text-3xl font-bold tabular">{j.total ?? 0}</div><div className="text-xs text-stone2">total</div></div>
                    </div>
                </div>
            </div>
        </div>
    );
}


export default function Reports() {
    const [range, setRange] = useState("7d");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        api.get("/reports/analytics", { params: { range } })
            .then(({ data }) => { if (mounted) setData(data); })
            .catch(() => { if (mounted) setData(null); })
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, [range]);

    const k = data?.kpis || {};
    const prev = data?.prev || null;
    const trend = useMemo(() => (data?.trend || []).map((t) => ({ ...t, label: fmtDateShort(t.date) })), [data]);
    const dirData = useMemo(() => ([
        { name: "Inbound", value: k.inbound || 0 },
        { name: "Outbound", value: k.outbound || 0 },
    ]), [k.inbound, k.outbound]);
    const hasDir = (k.inbound || 0) + (k.outbound || 0) > 0;
    const answerRate = pct(k.completed, k.total_calls);
    const prevAnswer = prev ? pct(prev.completed, prev.total_calls) : null;
    const prevAvg = prev && prev.completed ? Math.round(prev.total_duration_sec / prev.completed) : null;

    const exportXlsx = async () => {
        if (!data) return;
        try {
            const res = await api.get("/reports/export.xlsx", { params: { range }, responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
            const a = document.createElement("a");
            a.href = url; a.download = `inspiro-report-${range}.xlsx`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("Report exported to Excel");
        } catch (_e) {
            toast.error("Could not export the report");
        }
    };

    return (
        <AppShell>
            <div className="mx-auto max-w-6xl px-6 py-8 md:px-12 md:py-12" data-testid="reports-page">
                {/* Header */}
                <div className="mb-8 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
                    <div>
                        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">
                            <ChartBar size={14} weight="fill" /> Analytics
                        </div>
                        <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight sm:text-5xl">Reports.</h1>
                        <p className="mt-2 text-sm text-stone2">Team-wide performance across voice and WhatsApp.</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={exportXlsx}
                            disabled={!data || k.total_calls === 0}
                            data-testid="export-xlsx"
                            className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white px-3.5 py-2 text-xs font-semibold text-ink transition-colors hover:bg-stone-50 disabled:opacity-40"
                        >
                            <DownloadSimple size={14} weight="bold" /> Export Excel
                        </button>
                        <div className="inline-flex rounded-full border border-stone-300 bg-white p-1" data-testid="range-selector">
                            {RANGES.map((r) => (
                                <button
                                    key={r.id}
                                    data-testid={`range-${r.id}`}
                                    onClick={() => setRange(r.id)}
                                    className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all ${
                                        range === r.id ? "bg-ink text-white" : "text-stone2 hover:text-ink"
                                    }`}
                                >
                                    {r.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="grid place-items-center py-24 text-sm text-stone2" data-testid="reports-loading">Loading analytics…</div>
                ) : !data || k.total_calls === 0 ? (
                    <div className="grid place-items-center rounded-2xl border border-dashed border-stone-300 bg-white py-24 text-center" data-testid="reports-empty">
                        <Phone size={28} className="text-stone-300" />
                        <p className="mt-3 text-sm text-stone2">No calls in this period. Try a wider range.</p>
                    </div>
                ) : (
                    <>
                        {/* KPI cards */}
                        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
                            <KPICard label="Total Calls" value={k.total_calls} icon={Phone} accent={INK} testid="kpi-total"
                                delta={deltaPct(k.total_calls, prev?.total_calls)} />
                            <KPICard label="Answer Rate" value={`${answerRate}%`} sub={`${k.completed} of ${k.total_calls} connected`} icon={Target} accent={EMERALD} testid="kpi-answer-rate"
                                delta={deltaPct(answerRate, prevAnswer)} />
                            <KPICard label="Completed" value={k.completed} icon={CheckCircle} accent={EMERALD} testid="kpi-completed"
                                delta={deltaPct(k.completed, prev?.completed)} />
                            <KPICard label="Missed" value={k.no_answer} icon={PhoneIncoming} accent="#f59e0b" testid="kpi-missed" positiveIsGood={false} />
                            <KPICard label="Failed" value={k.failed} icon={XCircle} accent="#ef4444" testid="kpi-failed" positiveIsGood={false} />
                            <KPICard label="Talk Time" value={fmtDur(k.total_duration_sec)} icon={Clock} accent={BRAND} testid="kpi-talktime"
                                delta={deltaPct(k.total_duration_sec, prev?.total_duration_sec)} />
                            <KPICard label="Avg Duration" value={fmtDur(k.avg_duration_sec)} icon={Timer} accent={SKY} testid="kpi-avg"
                                delta={deltaPct(k.avg_duration_sec, prevAvg)} />
                            <KPICard label="Inbound" value={k.inbound} sub={`${k.outbound} outbound`} icon={PhoneIncoming} accent={SKY} testid="kpi-inbound" />
                        </div>

                        {/* Trend + direction */}
                        <div className="mb-6 grid gap-4 lg:grid-cols-3">
                            <div className="rounded-2xl border border-stone-200 bg-white p-5 lg:col-span-2" data-testid="trend-chart">
                                <div className="mb-4 text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Call volume trend</div>
                                <div className="h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <AreaChart data={trend} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                                            <defs>
                                                <linearGradient id="gOut" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor={BRAND} stopOpacity={0.35} />
                                                    <stop offset="95%" stopColor={BRAND} stopOpacity={0} />
                                                </linearGradient>
                                                <linearGradient id="gIn" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor={SKY} stopOpacity={0.3} />
                                                    <stop offset="95%" stopColor={SKY} stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: STONE }} axisLine={false} tickLine={false} />
                                            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: STONE }} axisLine={false} tickLine={false} />
                                            <Tooltip cursor={{ stroke: "#e7e5e4" }} contentStyle={{ borderRadius: 12, border: "1px solid #e7e5e4", fontSize: 12 }} />
                                            <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                                            <Area type="monotone" dataKey="outbound" stroke={BRAND} strokeWidth={2} fill="url(#gOut)" name="Outbound" />
                                            <Area type="monotone" dataKey="inbound" stroke={SKY} strokeWidth={2} fill="url(#gIn)" name="Inbound" />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                            <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="direction-chart">
                                <div className="mb-4 text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Inbound vs Outbound</div>
                                {hasDir ? (
                                    <div className="h-64">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie data={dirData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                                                    <Cell fill={SKY} />
                                                    <Cell fill={BRAND} />
                                                </Pie>
                                                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e7e5e4", fontSize: 12 }} />
                                                <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                ) : (
                                    <div className="grid h-64 place-items-center text-sm text-stone2">No direction data</div>
                                )}
                            </div>
                        </div>

                        {/* Busiest hours heatmap + provider breakdown */}
                        <div className="mb-6 grid gap-4 lg:grid-cols-3">
                            <div className="rounded-2xl border border-stone-200 bg-white p-5 lg:col-span-2" data-testid="heatmap-card">
                                <div className="mb-4 flex items-center gap-2">
                                    <GridFour size={15} weight="fill" className="text-brand" />
                                    <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Busiest hours <span className="normal-case tracking-normal text-stone-400">(UTC)</span></div>
                                </div>
                                <Heatmap data={data.heatmap} />
                            </div>
                            <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="provider-breakdown">
                                <div className="mb-4 flex items-center gap-2">
                                    <Phone size={15} weight="fill" className="text-brand" />
                                    <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">By provider</div>
                                </div>
                                <ProviderBreakdown providers={data.providers} />
                            </div>
                        </div>

                        {/* Leaderboard + dispositions */}
                        <div className="grid gap-4 lg:grid-cols-3">
                            <div className="rounded-2xl border border-stone-200 bg-white p-5 lg:col-span-2" data-testid="agent-leaderboard">
                                <div className="mb-4 flex items-center gap-2">
                                    <Trophy size={15} weight="fill" className="text-amber-500" />
                                    <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Agent leaderboard</div>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left">
                                        <thead>
                                            <tr className="border-b border-stone-100">
                                                <th className="pb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-stone2">Agent</th>
                                                <th className="pb-2 text-right text-[10px] font-bold uppercase tracking-[0.18em] text-stone2">Calls</th>
                                                <th className="pb-2 text-right text-[10px] font-bold uppercase tracking-[0.18em] text-stone2">Answer</th>
                                                <th className="pb-2 text-right text-[10px] font-bold uppercase tracking-[0.18em] text-stone2">Talk Time</th>
                                                <th className="pb-2 text-right text-[10px] font-bold uppercase tracking-[0.18em] text-stone2">Avg</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(data.agents || []).map((a, i) => {
                                                const rate = pct(a.completed, a.total);
                                                return (
                                                    <tr key={a.user_id || i} className="border-b border-stone-50" data-testid={`agent-stat-row-${i}`}>
                                                        <td className="py-3 text-sm font-medium">
                                                            <span className={`mr-2 inline-grid h-5 w-5 place-items-center rounded-full text-[10px] font-bold ${i === 0 ? "bg-amber-100 text-amber-700" : "bg-stone-100 text-stone2"}`}>{i + 1}</span>
                                                            {a.user_name}
                                                        </td>
                                                        <td className="py-3 text-right tabular text-sm">{a.total}</td>
                                                        <td className="py-3 text-right text-sm">
                                                            <span className="inline-flex items-center gap-2">
                                                                <span className="hidden h-1.5 w-12 overflow-hidden rounded-full bg-stone-100 sm:inline-block">
                                                                    <span className="block h-full rounded-full bg-emerald-500" style={{ width: `${rate}%` }} />
                                                                </span>
                                                                <span className="tabular text-stone-600">{rate}%</span>
                                                            </span>
                                                        </td>
                                                        <td className="py-3 text-right tabular text-sm">{fmtDur(a.total_duration_sec)}</td>
                                                        <td className="py-3 text-right tabular text-sm text-stone2">{fmtDur(a.avg_duration_sec)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="disposition-breakdown">
                                <div className="mb-4 text-[11px] font-bold uppercase tracking-[0.22em] text-stone2">Outcomes</div>
                                {(data.dispositions || []).length === 0 ? (
                                    <div className="grid h-40 place-items-center text-center text-sm text-stone2">No wrap-up dispositions logged yet.</div>
                                ) : (
                                    <div className="space-y-3">
                                        {(data.dispositions || []).map((d, i) => {
                                            const max = Math.max(...(data.dispositions || []).map((x) => x.count), 1);
                                            return (
                                                <div key={d.disposition} data-testid={`disposition-${i}`}>
                                                    <div className="mb-1 flex items-center justify-between text-xs">
                                                        <span className="font-medium text-ink">{d.disposition}</span>
                                                        <span className="tabular text-stone2">{d.count}</span>
                                                    </div>
                                                    <div className="h-2 overflow-hidden rounded-full bg-stone-100">
                                                        <div className="h-full rounded-full" style={{ width: `${(d.count / max) * 100}%`, backgroundColor: DISP_COLORS[i % DISP_COLORS.length] }} />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
                <AiStatsSection range={range} />
                <WhatsAppReportSection range={range} />
            </div>
        </AppShell>
    );
}
